import React from "react";
import { useLocation } from "react-router";
import { NavLink } from "react-router-dom";
import {
  Box,
  useMediaQuery,
  List,
  ListItem,
  Collapse,
  Typography,
  IconButton,
  styled,
  CSSObject,
  Theme,
  useTheme,
} from "@mui/material";
//@ts-ignore
import FeatherIcon from "feather-icons-react";
import { ISidebarProps } from "./types";
import { Scrollbar } from "../../../Components/customScroll/ScrollBar";
import { LogoIcon } from "../logo/Logo";
import {
  CustomListItemIcon,
  CustomTypography,
} from "../../../Components/formComponents";
import {
  SidebarMobileWidth,
  SidebarWidth,
} from "../../../assests/global/Theme-variable";
import MuiDrawer from "@mui/material/Drawer";
import { useSecureMenuList } from "../../../security";
import "./Sidebar.css";
import { IStoreState, useAppSelector } from "../../../redux";
import { PanelRightClose, PanelRightOpen } from "lucide-react";

const openedMixin = (theme: Theme): CSSObject => ({
  width: SidebarWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 12px) !important`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 12px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "90%",
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: SidebarWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    "& .MuiListItemIcon-root": {
      display: "flex",
      justifyContent: "center",
    },
    svg: {
      width: 19,
      height: 19,
    },
    p: {
      display: "none",
    },
  }),
}));

export const Sidebar: React.FC<ISidebarProps> = ({
  isMobileSidebarOpen,
  onSidebarClose,
  isSidebarOpen,
  toggleSidebar,
}) => {
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";
  const [open, setOpen] = React.useState<number[]>([]);
  const finalMenuList = useSecureMenuList();
  const { pathname } = useLocation();
  const pathDirect = pathname;
  const pathWithoutLastPart = pathname.slice(0, pathname.lastIndexOf("/"));
  const lgUp = useMediaQuery((theme: any) => theme.breakpoints.up("lg"));
  const customizer = useAppSelector((state: IStoreState) => state.customizer);

  // Theme-based colors
  const ACTIVE_COLOR = isLightMode ? "var(--secondary-color)" : "#fff";
  const INACTIVE_COLOR = isLightMode ? "#000000" : "#ffffff";
  const ACTIVE_MENU_BACKGROUND_COLOR = isLightMode 
    ? "linear-gradient(90deg, rgba(253, 118, 87, 0.2) 0%, rgba(253, 118, 87, 0.1) 100%)"
    : "linear-gradient(90deg, rgba(211, 211, 211, 0.3) 0%, rgba(211, 211, 211, 0.15) 100%)";
  const ACTIVE_MENU_HOVER_BACKGROUND = isLightMode
    ? "linear-gradient(90deg, rgba(211, 211, 211, 0.3) 0%, rgba(211, 211, 211, 0.15) 100%)"
    : "linear-gradient(90deg, rgba(211, 211, 211, 0.3) 0%, rgba(211, 211, 211, 0.15) 100%)";
  const LEFT_DRAWER_BACKGROUND_COLOR = isLightMode ? "#ffffff" : "#1E1E1E";
  const SELECTED_INDICATOR_COLOR = isLightMode ? "var(--secondary-color)" : "#fff";

  // Collapse all menus when sidebar is closed
  React.useEffect(() => {
    if (!isSidebarOpen) {
      setOpen([]);
    }
  }, [isSidebarOpen]);

  const handleClick = (index: number) => {
    if (!isSidebarOpen) {
      toggleSidebar();
      return;
    }

    const openedItems = [...open];
    const itemIndex = openedItems.indexOf(index);
    if (itemIndex > -1) {
      openedItems.splice(itemIndex, 1);
      setOpen(openedItems);
    } else {
      setOpen([...openedItems, index]);
    }
  };

  // Selected list item styles
  const getSelectedListItemStyles = (isSelected: boolean) => ({
    background: isSelected ? ACTIVE_MENU_BACKGROUND_COLOR : 'transparent',
    borderRadius: isSelected ? '9px' : 0,
    position: 'relative',
    '&:hover': {
      background: isSelected ? ACTIVE_MENU_HOVER_BACKGROUND : theme.palette.action.hover,
    },
    '&::before': isSelected ? {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: '3px',
      height: '20px',
      backgroundColor: SELECTED_INDICATOR_COLOR,
      borderRadius: '0 4px 4px 0',
    } : {},
    '& .MuiTypography-root': {
      fontWeight: isSelected ? 600 : 400,
    },
  });

  const SidebarContent = (
    <Scrollbar style={{ height: "calc(100vh - 5px)", paddingInline: 10 }}>
      <Box onClick={() => {
        if (!isSidebarOpen) {
          toggleSidebar();
        }
      }}>
        <Box>
          <List>
            {finalMenuList.map((item, index) => {
              if (true) {
                if (item.subHeader) {
                  return (
                    <ListItem key={item.subHeader}>
                      <CustomTypography 
                        variant="h6" 
                        sx={{ 
                          opacity: "0.4", 
                          color: isLightMode ? "#000" : "#fff" 
                        }}
                      >
                        {item.subHeader}
                      </CustomTypography>
                    </ListItem>
                  );
                } else if (item.children) {
                  const isChildSelected =
                    item.children.findIndex((x) =>
                      pathDirect.includes(x.href)
                    ) > -1;
                  return (
                    <React.Fragment key={item.title}>
                      <ListItem
                        button
                        component="li"
                        onClick={() => handleClick(index)}
                        selected={isChildSelected}
                        sx={{
                          margin: "0px 0px 8px",
                          padding: "12px 16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          minHeight: "45px",
                          ...getSelectedListItemStyles(isChildSelected),
                          ...(isChildSelected && {
                            borderRadius: "9px",
                            background: `${ACTIVE_MENU_BACKGROUND_COLOR} !important`,
                          }),
                        }}
                      >
                        <CustomListItemIcon
                          sx={{
                            minWidth: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: "8px",
                            color: isChildSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                            '& svg': {
                              color: isChildSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: isChildSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                              '& svg': {
                                color: isChildSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                              }
                            }}
                          >
                            <item.icon />
                          </Box>
                        </CustomListItemIcon>
                        <Box
                          display="flex"
                          justifyContent={"space-between"}
                          alignItems={"center"}
                          width={"100%"}
                        >
                          <Typography
                            sx={{
                              color: isChildSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                              fontWeight: pathWithoutLastPart === item.href ? 700 : 600,
                            }}
                          >
                            {item.title}
                          </Typography>
                          <FeatherIcon
                            icon={open.includes(index) || pathWithoutLastPart === item.href ? "chevron-down" : "chevron-right"}
                            size="16"
                            // color={isChildSelected ? ACTIVE_COLOR : INACTIVE_COLOR}
                          />
                        </Box>
                      </ListItem>
                      <Collapse
                        in={open.includes(index)}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List component="li" disablePadding>
                          {item.children.map((child) => {
                            const isChildItemSelected = pathDirect === child.href;
                            return (
                              <ListItem
                                key={child.title}
                                button
                                component={NavLink}
                                to={child.href}
                                onClick={onSidebarClose}
                                selected={isChildItemSelected}
                                sx={{
                                  mb: 1,
                                  ml: 2,
                                  padding: "6px 16px",
                           
                                  display: "flex",
                                  alignItems: "center",
                                  minHeight: "40px",
                                  gap: "10px",
                                  ...getSelectedListItemStyles(isChildItemSelected),
                                  ...(isChildItemSelected && {
                                    background: `${ACTIVE_MENU_BACKGROUND_COLOR} !important`,
                                    color: ACTIVE_COLOR,
                                  }),
                                }}
                              >
                                <CustomListItemIcon
                                  sx={{
                                    minWidth: "24px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: "8px",
                                    color: isChildItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                                    '& svg': {
                                      color: isChildItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                                    }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "20px",
                                      height: "20px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: isChildItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                                      '& svg': {
                                        color: isChildItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                                      }
                                    }}
                                  >
                                    <child.icon />
                                  </Box>
                                </CustomListItemIcon>

                                <Typography
                                  sx={{
                                    color: isChildItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                                    fontWeight: pathDirect.includes(child.href) ? 800 : 600,
                                  }}
                                >
                                  {child.title}
                                </Typography>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  );
                } else {
                  const isItemSelected = pathDirect === item.href;
                  return (
                    <List component="li" disablePadding key={item.title}>
                      <ListItem
                        button
                        component={NavLink}
                        to={item.href}
                        selected={isItemSelected}
                        sx={{
                          margin: "0px 0px 8px",
                          padding: "12px 16px",
                          display: "flex",
                          gap: "10px",
                          ...getSelectedListItemStyles(isItemSelected),
                          ...(isItemSelected && {
                            color: ACTIVE_COLOR,
                            background: `${ACTIVE_MENU_BACKGROUND_COLOR} !important`,
                            borderRadius: "9px",
                          }),
                        }}
                      >
                        <CustomListItemIcon
                          sx={{
                            minWidth: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: "8px",
                            color: isItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                            '& svg': {
                              color: isItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: isItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                              '& svg': {
                                color: isItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                              }
                            }}
                          >
                            <item.icon />
                          </Box>
                        </CustomListItemIcon>
                        <Typography
                          sx={{
                            color: isItemSelected ? ACTIVE_COLOR : INACTIVE_COLOR,
                            fontWeight: pathDirect === item.href ? 700 : 600,
                          }}
                          onClick={onSidebarClose}
                        >
                          {item.title}
                        </Typography>
                      </ListItem>
                    </List>
                  );
                }
              }
            })}
          </List>
        </Box>
      </Box>
    </Scrollbar>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open={isSidebarOpen}
        variant={"permanent"}
        sx={{
          width: SidebarWidth,
          zIndex: 1000,
          backgroundColor: customizer.activeMode === "dark" ? "#1E1E1E" : "#ffffff",
        }}
      >
        <DrawerHeader>
          {isSidebarOpen ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: 2,
              }}
              onClick={toggleSidebar}
            >
              <LogoIcon />
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                size="large"
                sx={{
                  ml: 3,
                  display: {
                    lg: "flex",
                    xs: "none",
                  },
                  color: isLightMode ? "#000" : "#fff",
                }}
              >
                <PanelRightOpen
                  color={customizer.activeMode === "dark" ? "#fff" : "#000"}
                />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ ml: 2 }} onClick={toggleSidebar}>
              <PanelRightClose
                color={customizer.activeMode === "dark" ? "#fff" : "#000"}
              />
            </Box>
          )}
        </DrawerHeader>
        <Box sx={{ marginTop: 5 }}>{SidebarContent}</Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      open={isMobileSidebarOpen}
      onClose={onSidebarClose}
      PaperProps={{
        sx: {
          zIndex: 50,
          width: SidebarMobileWidth,
          border: "0 !important",
          borderRight: 0,
          padding: "10px 10px",
          background: LEFT_DRAWER_BACKGROUND_COLOR,
        },
      }}
      variant="temporary"
    >
      {isSidebarOpen ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 2,
          }}
        >
          <LogoIcon />
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleSidebar}
            size="large"
            sx={{
              ml: 3,
              display: {
                lg: "flex",
                xs: "none",
              },
              color: isLightMode ? "#000" : "#fff",
            }}
          >
            <PanelRightOpen
              color={customizer.activeMode === "dark" ? "#fff" : "#000"}
            />
          </IconButton>
        </Box>
      ) : (
        <>
          <PanelRightClose
            color={customizer.activeMode === "dark" ? "#fff" : "#000"}
          />
        </>
      )}
      <Box sx={{ marginTop: 5 }}>{SidebarContent}</Box>
    </Drawer>
  );
};
